import type { BioDocument } from './model';
import { SITE_URL } from '@/lib/seo/schema';

export const BIO_DIRECTORY_TAG = 'bio-directory';
export const CREATOR_PAGE_SIZE = 48;

/** A published page in the directory. ownerId stays on the server for connection lookups. */
export type BioDirectoryEntry = {
  slug: string;
  name: string;
  description: string;
  avatar: string;
  avatarShape: NonNullable<BioDocument['avatarShape']>;
  accent: string;
  socials: NonNullable<BioDocument['socials']>;
  showFollowerCount: boolean;
  ownerId: string;
};

export function bioUrl(slug: string) {
  return `${SITE_URL}/bio/${encodeURIComponent(slug)}`;
}

export function bioRedirectPath(
  slug: string,
  query: Record<string, string | string[] | undefined>,
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    for (const item of Array.isArray(value) ? value : value === undefined ? [] : [value])
      search.append(key, item);
  }
  return `/bio/${encodeURIComponent(slug)}${search.size ? `?${search}` : ''}`;
}

/** Only creator-published text. Hidden sections must never supply search snippets. */
export function bioDescription(document: BioDocument) {
  if (document.bio.trim()) return document.bio.trim();
  const titles = document.blocks
    .filter((block) => block.visible && (block.type === 'link' || block.type === 'collection'))
    .slice(0, 3)
    .map((block) => block.title);
  return titles.length
    ? `Links from ${document.name}: ${titles.join(', ')}.`
    : `Explore ${document.name}’s links.`;
}

/** Links a visitor can open: visible link blocks and collection items. */
function publicLinkCount(document: BioDocument) {
  const visible = document.blocks.filter((block) => block.visible);
  const links = visible.filter((block) => block.type === 'link').length;
  const items = visible.reduce(
    (count, block) => count + (block.type === 'collection' ? (block.items?.length ?? 0) : 0),
    0,
  );
  return links + items;
}

/**
 * Search engines judge a site partly by its weakest indexed pages. A published page with
 * almost nothing on it still works at its address, but stays out of search, the sitemap
 * and the creator directory until it has a biography and a link, or three links. Social icons
 * do not count: they only render while the account stays connected.
 */
export function isBioIndexable(document: BioDocument) {
  const links = publicLinkCount(document);
  return links >= 3 || (Boolean(document.bio.trim()) && links >= 1);
}

/** Pass the public projection so sameAs matches the social links actually shown. */
export function bioProfileSchema(slug: string, document: BioDocument) {
  const url = bioUrl(slug);
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${url}#profile`,
    url,
    name: `${document.name} | ${document.showBranding ? 'Links on OwlScran' : 'Links'}`,
    description: bioDescription(document),
    mainEntity: {
      '@type': 'Person',
      '@id': `${url}#creator`,
      name: document.name,
      url,
      description: bioDescription(document),
      ...(document.avatar ? { image: document.avatar } : {}),
      ...(document.socials?.length ? { sameAs: document.socials.map((social) => social.url) } : {}),
    },
  };
}

export function creatorDirectoryPath(page: number) {
  return page === 1 ? '/creators' : `/creators?page=${page}`;
}

export function creatorDirectoryPage(value: string | string[] | undefined) {
  if (value === undefined) return 1;
  if (typeof value !== 'string' || !/^[1-9]\d{0,5}$/.test(value)) return null;
  return Number(value);
}
